import { useEffect, useRef } from "react";
import "./CustomCursor.css";

const HOVER_TARGETS =
  "a, button, [role='button'], input, textarea, select, label, " +
  ".skill-card, .project-card, .blog-card, .expertise-card, " +
  ".details-container, .details-container-selected, " +
  ".info-item, .hero-btn-primary, .hero-btn-outline, " +
  ".theme-toggle-btn, .theme-toggle-icon, .nav-icon-link, " +
  ".logo-wrap, .hamburger-icon, .ftag, .social-icon, .hero-social-icon, " +
  ".button-img, .load-more button, .mobile-menu a";

const CustomCursor = () => {
  const dotRef  = useRef(null);
  const ringRef = useRef(null);
  const state   = useRef({
    mx: -200, my: -200,
    rx: -200, ry: -200,
    raf: null,
    hovered: false,
  });

  useEffect(() => {
    /* ── Only on pointer devices ── */
    if (window.matchMedia("(hover: none) and (pointer: coarse)").matches) return;

    const dot  = dotRef.current;
    const ring = ringRef.current;
    const s    = state.current;

    const lerp = (a, b, t) => a + (b - a) * t;

    /* ── Mouse position → dot is instant ── */
    const onMove = ({ clientX: x, clientY: y }) => {
      s.mx = x; s.my = y;
      dot.style.transform = `translate(${x}px,${y}px) translate(-50%,-50%)`;
    };

    /* ── Cursor enters / leaves window ── */
    const onEnter = () => { dot.style.opacity = "1"; ring.style.opacity = "1"; };
    const onLeave = () => { dot.style.opacity = "0"; ring.style.opacity = "0"; };

    /* ── Mouse down / up ── */
    const onDown = () => { dot.classList.add("c-click");  ring.classList.add("c-click");  };
    const onUp   = () => { dot.classList.remove("c-click"); ring.classList.remove("c-click"); };

    /* ── Hover detection via delegation (works on dynamic DOM) ── */
    const onOver = (e) => {
      if (!s.hovered && e.target.closest(HOVER_TARGETS)) {
        s.hovered = true;
        dot.classList.add("c-hover");
        ring.classList.add("c-hover");
      }
    };
    const onOut = (e) => {
      if (s.hovered && e.target.closest(HOVER_TARGETS)) {
        s.hovered = false;
        dot.classList.remove("c-hover");
        ring.classList.remove("c-hover");
      }
    };

    /* ── rAF loop: ring lags behind mouse ── */
    const tick = () => {
      s.rx = lerp(s.rx, s.mx, 0.18);
      s.ry = lerp(s.ry, s.my, 0.18);
      ring.style.transform = `translate(${s.rx}px,${s.ry}px) translate(-50%,-50%)`;
      s.raf = requestAnimationFrame(tick);
    };

    document.addEventListener("mousemove",  onMove,  { passive: true });
    document.addEventListener("mouseenter", onEnter);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mousedown",  onDown);
    document.addEventListener("mouseup",    onUp);
    document.addEventListener("mouseover",  onOver,  { passive: true });
    document.addEventListener("mouseout",   onOut,   { passive: true });

    tick();

    return () => {
      document.removeEventListener("mousemove",  onMove);
      document.removeEventListener("mouseenter", onEnter);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mousedown",  onDown);
      document.removeEventListener("mouseup",    onUp);
      document.removeEventListener("mouseover",  onOver);
      document.removeEventListener("mouseout",   onOut);
      cancelAnimationFrame(s.raf);
    };
  }, []);

  return (
    <>
      <div ref={dotRef}  className="c-dot"  aria-hidden="true" />
      <div ref={ringRef} className="c-ring" aria-hidden="true" />
    </>
  );
};

export default CustomCursor;
